import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { PropertyInfoPanel, addressGroupDef } from './PropertyInfoPanel';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Compact fallback for the Anyone Home tour-type step.
 * A tall fixed height (e.g. 1000px) left a large empty white band under the
 * short tour-type UI; we start small and grow when the embed posts a height.
 */
const DEFAULT_ANYONEHOME_IFRAME_HEIGHT = 320;
const MIN_ANYONEHOME_IFRAME_HEIGHT = 200;
const MAX_ANYONEHOME_IFRAME_HEIGHT = 1400;

function parseHeightPx(raw: string | null | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = parseInt(String(raw).replace(/px$/i, '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function clampHeight(height: number): number {
  return Math.min(
    MAX_ANYONEHOME_IFRAME_HEIGHT,
    Math.max(MIN_ANYONEHOME_IFRAME_HEIGHT, Math.ceil(height)),
  );
}

function extractReportedHeight(data: unknown): number | null {
  if (typeof data === 'number' && data > 0) return data;
  if (typeof data === 'string') {
    const n = parseInt(data, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (!data || typeof data !== 'object') return null;

  const obj = data as Record<string, unknown>;

  // iframely / common: { method: 'resize', height }
  if (
    (obj.method === 'resize' || obj.type === 'resize' || obj.event === 'resize') &&
    typeof obj.height === 'number' &&
    obj.height > 0
  ) {
    return obj.height;
  }

  if (typeof obj.height === 'number' && obj.height > 0) return obj.height;
  if (typeof obj.iframeHeight === 'number' && obj.iframeHeight > 0) return obj.iframeHeight;

  for (const key of ['data', 'payload', 'message'] as const) {
    const nested = obj[key];
    if (nested && typeof nested === 'object') {
      const h = (nested as Record<string, unknown>).height;
      if (typeof h === 'number' && h > 0) return h;
    }
    // Some widgets send payload as [height] or [[height, width]]
    if (Array.isArray(nested)) {
      const first = nested[0];
      if (typeof first === 'number' && first > 0) return first;
      if (Array.isArray(first) && typeof first[0] === 'number' && first[0] > 0) {
        return first[0];
      }
    }
  }

  return null;
}

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;

  const anyonehomeUrlRaw = useContentToken('scheduletourform.anyonehome_scheduler_url');
  const anyonehomeUrl = (anyonehomeUrlRaw || '').trim();
  const heightToken = useContentToken('scheduletourform.anyonehome_iframe_height');
  const fallbackHeight = parseHeightPx(heightToken, DEFAULT_ANYONEHOME_IFRAME_HEIGHT);

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(fallbackHeight);
  const [hasReportedHeight, setHasReportedHeight] = useState(false);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  useEffect(() => {
    setHasReportedHeight(false);
    setIframeHeight(fallbackHeight);
  }, [fallbackHeight, anyonehomeUrl]);

  // Grow/shrink when the embed reports its content height.
  useEffect(() => {
    if (!anyonehomeUrl) return;

    let embedOrigin = '';
    try {
      embedOrigin = new URL(anyonehomeUrl).origin;
    } catch {
      embedOrigin = '';
    }

    const onMessage = (event: MessageEvent) => {
      if (embedOrigin && event.origin !== embedOrigin) return;
      if (iframeRef.current && event.source && event.source !== iframeRef.current.contentWindow) {
        return;
      }

      let data: unknown = event.data;
      if (typeof data === 'string') {
        if (data.startsWith('[iFrameSizer]')) {
          const parts = data.replace('[iFrameSizer]', '').split(':');
          const h = parseInt(parts[1], 10);
          if (Number.isFinite(h) && h > 0) {
            setHasReportedHeight(true);
            setIframeHeight(clampHeight(h));
          }
          return;
        }
        try {
          data = JSON.parse(data);
        } catch {
          const n = parseInt(data as string, 10);
          if (Number.isFinite(n) && n > 0) {
            setHasReportedHeight(true);
            setIframeHeight(clampHeight(n));
          }
          return;
        }
      }

      const height = extractReportedHeight(data);
      if (height) {
        setHasReportedHeight(true);
        setIframeHeight(clampHeight(height));
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [anyonehomeUrl]);

  return (
    <section className="cf-section stf-section">
      <div ref={sectionRef} className="reveal-fade-up cf-container">
        <div className="cf-grid">
          {/* Form first in the DOM so it stacks above contact on mobile/tablet. */}
          <div className="cf-form-col">
            {anyonehomeUrl ? (
              <EditableToken
                path="scheduletourform.anyonehome_scheduler_url"
                type="url"
                as="div"
                className="stf-anyonehome"
              >
                <iframe
                  ref={iframeRef}
                  src={anyonehomeUrl}
                  title="Schedule a Tour"
                  width="100%"
                  height={iframeHeight}
                  loading="lazy"
                  // Allow inner scroll until/unless the embed reports a real height.
                  scrolling={hasReportedHeight ? 'no' : 'yes'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  className="stf-anyonehome-iframe"
                  style={{ height: `${iframeHeight}px` }}
                />
              </EditableToken>
            ) : (
              <EditableToken path={formBlockPath} as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            )}
          </div>

          {/* Contact info: below the form when stacked; left column on desktop. */}
          <div className="cf-info-col">
            <PropertyInfoPanel prefix={prefix} />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="scheduletourform.form_block" as="div" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.anyonehome_scheduler_url" type="url" as="div" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.anyonehome_iframe_height" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.visit_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup groupPath="scheduletourform.address" definition={addressGroupDef} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableToken path="scheduletourform.call_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.phone" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.hours_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableCollectionBinding bindingPath="scheduletourform.office_hours" label="Office Hours" className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableCollectionBinding>

      <style>{`
        .stf-section.cf-section {
          padding: 1.5rem 0 2.5rem;
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .stf-section.cf-section { padding: 2rem 0 3rem; }
        }
        .stf-section .cf-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px)  { .stf-section .cf-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .stf-section .cf-container { padding: 0 2rem; } }

        .stf-section .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-areas: "form" "info";
          gap: 1.5rem;
        }
        @media (min-width: 1024px) {
          .stf-section .cf-grid {
            grid-template-columns: 5fr 7fr;
            grid-template-areas: "info form";
            gap: 2rem;
            align-items: start;
          }
        }
        .stf-section .cf-form-col { grid-area: form; position: relative; }
        .stf-section .cf-info-col { grid-area: info; }

        .stf-section .cf-form-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          padding: 2rem;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }
        .stf-section .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
        .stf-anyonehome {
          width: 100%;
          background: transparent;
          border-radius: var(--vibe-radius-lg, 12px);
        }
        .stf-anyonehome-iframe {
          display: block;
          width: 100%;
          border: none;
          background: transparent;
          vertical-align: top;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
      description:
        'Used when no Anyone Home scheduler URL is set on the property. Prefer custom_anyonehome_scheduler_url when available.',
    },
    'scheduletourform.anyonehome_scheduler_url': {
      label: 'Anyone Home Scheduler URL',
      type: 'url',
      defaultField: 'custom_anyonehome_scheduler_url',
      suggestedFields: ['custom_anyonehome_scheduler_url'],
      defaultValue: '',
      description:
        'When set, embeds this URL in place of the schedule-a-tour form block.',
    },
    'scheduletourform.anyonehome_iframe_height': {
      label: 'Anyone Home Iframe Height (px)',
      type: 'text',
      defaultValue: '320',
      description:
        'Initial height for the Anyone Home embed (tour-type step). The iframe grows when the embed reports its height; raise this if the calendar step feels cramped.',
    },
    'scheduletourform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduletourform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduletourform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'scheduletourform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': {
      label: 'Address',
      fields: {
        text: {
          label: 'Address Text',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '',
        },
        url: {
          label: 'Map URL',
          type: 'url',
          // URL has no defaultField — defaults to usePropertyAddressUrl() at render
          // time. Editor overrides still win.
          defaultValue: '',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_blank',
        },
      },
    },
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
